import type { Timestamp } from 'firebase/firestore'

/**
 * The session clock, stored as an anchor rather than as a value.
 *
 * See timing/internal/store/clock.go, which writes these. The short version:
 * the clock is the only thing on a dashboard that changes every second, so
 * storing it per second would be a write per second for a value nobody can
 * read that fast. Instead the interpreter records "at this instant the timing
 * system said this much remained, and the clock was or was not running", and
 * the browser counts from there.
 *
 * A single "the race started at X and lasts Y" would be simpler and is wrong
 * the moment the clock stops: a red flag stops the countdown while the wall
 * clock keeps going, and everything derived from a start time is then wrong by
 * the length of the stoppage for the rest of the race.
 */
export interface ClockCheckpointDoc {
  at: Timestamp
  elapsedMs: number
  remainingMs: number
  running: boolean
}

/** A checkpoint once read back, with the timestamp resolved. */
export interface ClockCheckpoint {
  at: Date
  elapsedMs: number
  remainingMs: number
  running: boolean
}

/** What the clock reads at a given moment. */
export interface SessionClock {
  elapsedMs: number
  /** Never negative: a countdown that has expired reads zero, not -00:12. */
  remainingMs: number
  running: boolean
}

/**
 * Read the clock at `now` by extrapolating from the last checkpoint.
 *
 * When the checkpoint says the clock was stopped, both values are held still —
 * which is the entire point, and the thing a naive countdown gets wrong.
 */
export function readClock(cp: ClockCheckpoint | undefined | null, now: Date): SessionClock | null {
  if (!cp) return null

  const since = cp.running ? Math.max(0, now.getTime() - cp.at.getTime()) : 0
  return {
    elapsedMs: Math.max(0, cp.elapsedMs + since),
    // Clamped at zero. Past the end of a session the checkpoint stops being
    // refreshed, and an unclamped value would count on into negative numbers.
    remainingMs: Math.max(0, cp.remainingMs - since),
    running: cp.running,
  }
}

/**
 * How far the viewer's clock is from the clock that wrote the checkpoints.
 *
 * A checkpoint is only as good as the clock it is compared against, and a
 * laptop on a pit wall is exactly the machine whose clock is wrong — often by
 * minutes, and nobody notices until a countdown disagrees with the one on the
 * timing screen. So rather than trusting `Date.now()`, take an offset from the
 * timestamps the capture job is already writing on every poll.
 *
 * Each sample understates the offset by however long the value took to reach
 * us — capture, interpret, write, push — so the *largest* recent sample is the
 * most accurate, in the same way a ping is judged by its minimum. What that
 * leaves is a residual of roughly one pipeline latency, about a second, which
 * is nothing next to the wrong-by-minutes case this exists to fix.
 */
export class ServerClock {
  /** Below this, assume the viewer's clock is simply right. */
  static readonly deadbandMs = 2000

  /*
   * Above this, the sample is not a clock reading.
   *
   * This class answers "how far is this browser's clock from the clock that
   * stamped the data", and it is fed timestamps from documents. On a live
   * session those timestamps were written a second ago and the difference is
   * skew. On a session captured in May they were written in May, and the
   * difference is not skew at all — it is how long ago the racing was.
   *
   * Told the difference anyway, this class reports a "now" in May. That has now
   * cost two separate faults: a stint chart that drew a fifteen-minute stint as
   * a thousand hours, and a time-of-day tile that sat at 16:21 on a Tuesday
   * evening because the race it was pointed at had finished on Sunday. Both
   * times the mitigation was a comment telling callers to pass the browser's
   * clock instead, and both times a later caller did not.
   *
   * So the rule moves here, where it cannot be forgotten. The two errors are
   * not equally bad: failing to correct a real skew leaves a clock as wrong as
   * it already was, while accepting a stale document puts it days out and
   * confidently so.
   *
   * Ten minutes is the bound because it is well past any drift that goes
   * unnoticed — the case this exists for is a clock quietly a minute or two
   * out, not one visibly set to the wrong time — and nowhere near the hours or
   * days that separate a browser from a session that has ended. The precise
   * guard is at the call site, which knows whether capture is still live; this
   * is the backstop that stops the next caller reintroducing the fault.
   */
  static readonly plausibleSkewMs = 10 * 60_000

  private samples: number[] = []
  private readonly window: number

  /** How many recent samples to consider. At roughly one a second, a minute. */
  constructor(window = 60) {
    this.window = window
  }

  /**
   * Offer an instant stamped by the capture job, e.g. a bucket's lastSeenAt.
   *
   * Ignored unless it could be a reading of now — see plausibleSkewMs.
   */
  sample(serverNow: Date, browserNow: Date = new Date()): void {
    const offset = serverNow.getTime() - browserNow.getTime()
    if (Math.abs(offset) > ServerClock.plausibleSkewMs) return
    this.samples.push(offset)
    if (this.samples.length > this.window) this.samples.shift()
  }

  /**
   * Forget every sample.
   *
   * Called when the view moves to a different session, because samples belong
   * to the bucket they came from. Carrying them across is how a page opened on
   * Saturday's qualifying showed a time of day taken from Sunday's race.
   */
  reset(): void {
    this.samples = []
  }

  get offsetMs(): number {
    if (this.samples.length === 0) return 0
    const best = Math.max(...this.samples)
    return Math.abs(best) < ServerClock.deadbandMs ? 0 : best
  }

  /** Now, on the clock that wrote the checkpoints. */
  now(browserNow: Date = new Date()): Date {
    return new Date(browserNow.getTime() + this.offsetMs)
  }
}

/**
 * A duration as a clock reads it: h:mm:ss, or m:ss under an hour.
 *
 * Race clocks are read at a glance from two metres away, so the leading unit is
 * never zero-padded — "1:04:22" not "01:04:22" — and everything after it always
 * is, so the digits do not move.
 */
export function formatDuration(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000))
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  const pad = (n: number) => String(n).padStart(2, '0')
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`
}

/**
 * A lap or sector time as a timing screen shows it: m:ss.SSS, or ss.SSS for
 * anything under a minute, which is most sector times.
 *
 * Thousandths, not hundredths. A lap is won by less than a hundredth often
 * enough that rounding it away would be discarding the answer.
 */
export function formatLapTime(ms: number | null | undefined): string {
  if (ms == null || ms <= 0) return '—'
  const total = Math.floor(ms)
  const m = Math.floor(total / 60000)
  const s = Math.floor((total % 60000) / 1000)
  const thousandths = total % 1000
  const frac = String(thousandths).padStart(3, '0')
  return m > 0 ? `${m}:${String(s).padStart(2, '0')}.${frac}` : `${s}.${frac}`
}

/**
 * A gap or interval: "+1.234", "+2 laps", or an em dash for the leader.
 *
 * Laps take precedence over time when both are present, because "+2 laps" is
 * the true answer and the seconds figure alongside it is a fragment of the
 * current lap that reads as a much smaller gap than it is.
 */
export function formatGap(ms: number | null | undefined, laps = 0): string {
  if (laps > 0) return `+${laps} lap${laps === 1 ? '' : 's'}`
  if (ms == null || ms <= 0) return '—'
  return `+${formatLapTime(ms)}`
}

/**
 * A time of day as a dashboard shows one: 24-hour, two digits, seconds only
 * where asked.
 *
 * Eight tiles and screens each spelled out the same toLocaleTimeString options,
 * and three of them had quietly settled on the locale's 12-hour default. A pit
 * wall reads a clock the way the timing screen prints one.
 */
export function formatClock(d: Date, opts: { seconds?: boolean } = {}): string {
  return d.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    ...(opts.seconds ? { second: '2-digit' as const } : {}),
    hour12: false,
  })
}
